import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {getMyRoleWeight, getSession, getEhrInfo, getAllVendors} from '../../services/session';
import { iAmHigherAuthority, reloadSessionCorp, setRoleInfo } from '../../libs.jsx';
import { useNotifications } from '@/contexts/NotificationContext.jsx';
import { fetchUserRoles } from '../../pages/customers/campuses/campus-profile/api/campusProfileApi.js';
import { isHelpAvailable, openHelpPicker, HELP_ACTIVE_EVENT } from '../help/helpAvailability.js';
import Logout from '../Logout.jsx';
import ThemeToggle from '../ThemeToggle/ThemeToggle.jsx';
import { useBrandLogo } from '../useBrandLogo.js';
import './navigation-v2.css';

const NavigationV2 = () => {
    const logo = useBrandLogo();
    const location = useLocation();
    const { activeAlarms, activeCloudAlarms } = useNotifications();
    const [openSections, setOpenSections] = useState({});

    // Mobile drawer state — never persisted
    const [mobileOpen, setMobileOpen] = useState(false);

    // Impersonate role-switch dialog state
    const [impersonateOpen, setImpersonateOpen] = useState(false);
    const [impersonateRoleWeight, setImpersonateRoleWeight] = useState('');
    const [impersonating, setImpersonating] = useState(false);
    const [roleOptions, setRoleOptions] = useState([]);

    // Close mobile drawer on route change
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    /* Lit while the helper is on screen — picker open or a flow running. The
       guide owns that state and lives in a different tree, so it arrives by
       event (help/helpAvailability.js), the same way the open request travels
       outward. */
    const [helpActive, setHelpActive] = useState(false);
    useEffect(() => {
        const onHelpActive = (e) => setHelpActive(Boolean(e?.detail?.active));
        window.addEventListener(HELP_ACTIVE_EVENT, onHelpActive);
        return () => window.removeEventListener(HELP_ACTIVE_EVENT, onHelpActive);
    }, []);

    // Lock body scroll when mobile drawer is open
    useEffect(() => {
        if (mobileOpen) {
            document.body.classList.add('nav2-body-locked');
        } else {
            document.body.classList.remove('nav2-body-locked');
        }
        return () => document.body.classList.remove('nav2-body-locked');
    }, [mobileOpen]);

    // Session data (same pattern as V1 Navigation)
    const dData = getSession();

    const closeMobileNav = useCallback(() => {
        setMobileOpen(false);
    }, []);

    // Filtered roles for impersonate dialog (only roles the user outranks)
    const filteredRoles = useMemo(() => {
      return (roleOptions || [])
        .filter((r) => iAmHigherAuthority(r.user_role_weight, dData?.my_role_weight))
        .sort((a, b) => (a.user_role_weight || 0) - (b.user_role_weight || 0));
    }, [roleOptions, dData?.my_role_weight]);

    /* Does this user get the Guided Help entry? Memoised because the check reads
       (and therefore decrypts) the session, and this component re-renders on every
       route change and alarm update. Campus switches move `acnt`; impersonation
       moves `role_weight`; either can change which flows are available. This nav
       is v2-only, so the version half of the gate is implicit. */
    const helpAvailable = useMemo(
        () => (dData?.acnt ? isHelpAvailable('v2') : false),
        [dData?.acnt, dData?.role_weight],
    );

    // Open the impersonate role-switch dialog (lazy-fetch roles)
    const handleOpenImpersonateDialog = useCallback(async () => {
      setImpersonateRoleWeight(dData?.role_weight ?? dData?.my_role_weight ?? '');
      setImpersonating(false);
      setImpersonateOpen(true);
      // Fetch roles if not already loaded
      if (roleOptions.length === 0) {
        try {
          const roles = await fetchUserRoles(dData?.acnt);
          setRoleOptions(roles || []);
        } catch (err) {
          console.error('[NavigationV2] Failed to fetch roles:', err);
        }
      }
    }, [dData?.role_weight, dData?.my_role_weight, dData?.acnt, roleOptions.length]);

    // Confirm impersonation with the selected role
    const handleConfirmImpersonate = useCallback(() => {
      if (!dData) return;
      setImpersonating(true);
      const selectedWeight = parseInt(impersonateRoleWeight, 10);
      // Find the selected role name from fetched options
      const selectedRole = (roleOptions || []).find((r) => r.user_role_weight === selectedWeight);
      const roleName = selectedRole?.user_role_name || '';
      setRoleInfo(selectedWeight, dData, roleName);
      reloadSessionCorp(dData.corp, 0, dData.cognito, dData, dData.acnt);
    }, [dData, impersonateRoleWeight, roleOptions]);

    /* Every hook above this line, none below. The guard flips when a session
       arrives or ends, and a hook after it changes the hook count between
       renders, which React rejects and takes the whole nav down. */
    if (!dData?.acnt) return null;

    const toggle = (key) => {
        setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const isActive = (path) => location.pathname === path;
    const isActivePrefix = (prefix) => location.pathname.startsWith(prefix);

    // Alert count — same logic as V1 Navigation
    const campus = dData.campusData[dData.acnt.toString()];
    const ehr = getEhrInfo();
    const isCloud = campus?.is_cloud;
    const alarmItems = isCloud ? activeCloudAlarms : activeAlarms;
    const myGroupIds = isCloud ? dData.groups : dData?.groupGuids;
    const showAllAlertsByRole = iAmHigherAuthority(10, dData.role_weight);
    const alertCount = showAllAlertsByRole
        ? alarmItems.length
        : alarmItems.filter((alarm) => {
              const ids = isCloud ? alarm?.group_ids : alarm?.GroupIds;
              return ids?.some((gid) => myGroupIds?.includes(gid));
          })?.length;

    /* Impersonating when EITHER the corp differs from your own OR the acting role
       weight differs from your real one.

       The role half is what makes impersonating a role on your OWN corp usable: the
       banner is the only way back, so without it the session was stuck in the
       borrowed role until logout. The corp half alone missed that case entirely.

       Weights are compared as numbers because one side comes from
       parseInt(impersonateRoleWeight) and the other straight off the session
       payload, so a 30 vs "30" pairing would otherwise read as a difference.

       `weightOf` returns null for an absent value rather than leaning on Number():
       Number(null) is 0, which is finite, so a session missing my_role_weight would
       have compared 0 against a real weight and raised the banner on a user who is
       not impersonating anything. Both sides must be present for a difference to
       count.

       These two can only diverge through impersonation: login and every same-corp
       session reload assign role_weight and my_role_weight from the same
       resp.RoleWeight (ApiRequester.jsx:77-80, :143-144), and only setRoleInfo and
       the cross-corp reload branch move one without the other. */
    const isImpersonating = (() => {
        const weightOf = (v) => {
            if (v === null || v === undefined || v === '') return null;
            const n = Number(v);
            return Number.isFinite(n) ? n : null;
        };
        const corpDiffers = Boolean(dData?.corp)
            && dData?.myCorp?.toString() !== dData?.corp?.toString();
        const myWeight = weightOf(dData?.my_role_weight);
        const actingWeight = weightOf(dData?.role_weight);
        const roleDiffers = myWeight !== null && actingWeight !== null
            && myWeight !== actingWeight;
        return corpDiffers || roleDiffers;
    })();

    const sidebarClasses = `nav2-sidebar${mobileOpen ? ' is-mobile-open' : ''}`;

    return (
        <>
            {/* ── Mobile Hamburger Button ── */}
            <button
                className="nav2-mobile-toggle"
                onClick={() => setMobileOpen((prev) => !prev)}
                aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
            >
                <i className={mobileOpen ? 'fe fe-x' : 'fe fe-menu'} />
            </button>

            {/* ── Mobile Backdrop ── */}
            <div
                className={`nav2-backdrop${mobileOpen ? ' is-visible' : ''}`}
                onClick={closeMobileNav}
            />

            <aside className={sidebarClasses}>
                {/* ── Impersonation Banner ── */}
                {isImpersonating && (
                    <div className="nav2-impersonate">
                        <div className="nav2-impersonate__label">
                            <span>Impersonating</span>
                            <button
                                className="nav2-impersonate__icon-btn"
                                onClick={handleOpenImpersonateDialog}
                                title="Change impersonation role"
                                aria-label="Change impersonation role"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                    <circle cx="12" cy="10" r="3" />
                                    <path d="M12 13c-2.5 0-4 1.5-4 3" />
                                </svg>
                            </button>
                            {dData.role_name ? (
                                <>
                                    <span className="nav2-impersonate__role">as</span>
                                    <span className="nav2-impersonate__role">{dData.role_name}</span>
                                </>
                            ) : null}
                        </div>
                        <button
                            className="nav2-impersonate__stop"
                            onClick={() => {
                                setRoleInfo(dData.my_role_weight, dData, '');
                                reloadSessionCorp(dData.myCorp, -99, dData.cognito, dData, dData.myAcnt);
                            }}
                        >
                            Stop
                        </button>
                    </div>
                )}

                {/* ── Brand ── */}
                <div className="nav2-brand">
                    <img src={logo} alt="PalCare" className="nav2-brand__logo" />
                    <div className="nav2-brand__text">
                        <span className="nav2-brand__campus">
                            {dData.corporateName || dData.campus}
                            {iAmHigherAuthority(99, dData?.my_role_weight) && dData?.acnt ? (
                                <Link
                                    to={`/profiles/campus/${dData.acnt}`}
                                    className="nav2-brand__campus-link"
                                    title="View campus profile"
                                >
                                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955a1.126 1.126 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                                    </svg>
                                </Link>
                            ) : null}
                        </span>
                    </div>
                </div>

                {/* ── Scrollable Body ── */}
                <div className="nav2-body">
                    {/* ADMIN PANEL — super admins only */}
                    {iAmHigherAuthority(80, getMyRoleWeight()) ? (
                        <div className="nav2-section">
                            {iAmHigherAuthority(99, getMyRoleWeight()) ? (
                                <>
                                    <span className="nav2-section-label">PalCare Admin Panel</span>
                                    <button
                                        className={`nav2-expand-btn${isActivePrefix('/customers') ? ' is-active' : ''}${openSections.admin ? ' is-open' : ''}`}
                                        onClick={() => toggle('admin')}
                                        title="Customers"
                                    >
                                        <i className="fe fe-aperture nav2-expand-icon" />
                                        <span className="nav2-item__text">Customers</span>
                                        <i className="fe fe-chevron-right nav2-expand-chevron" />
                                    </button>
                                    <div className={`nav2-subnav${openSections.admin ? ' is-open' : ''}`}>
                                        {/* Single grid item: see .nav2-subnav in navigation-v2.css. */}
                                        <div className="nav2-subnav__inner">
                                            <Link to="/customers/corporations" className={`nav2-subitem${isActive('/customers/corporations') ? ' is-active' : ''}`} onClick={closeMobileNav}>
                                                Corporations
                                            </Link>
                                            <Link to="/customers/campuses" className={`nav2-subitem${isActive('/customers/campuses') ? ' is-active' : ''}`} onClick={closeMobileNav}>
                                                Campuses
                                            </Link>
                                            <Link to="/customers/servers" className={`nav2-subitem${isActive('/customers/servers') ? ' is-active' : ''}`} onClick={closeMobileNav}>
                                                Servers
                                            </Link>
                                        </div>
                                    </div>
                                </>
                            ) : <></>}
                            <Link to="/demo" className={`nav2-item${isActive('/demo') ? ' is-active' : ''}`} onClick={closeMobileNav} title="Demo Panel">
                                <i className="fe fe-airplay nav2-item__icon" />
                                <span className="nav2-item__text">Demo Panel</span>
                            </Link>
                        </div>
                    ) : <></>}

                    {/* ── MAIN ── */}
                    <div className="nav2-section">
                        <span className="nav2-section-label">Main</span>

                        {/* Dashboard — expandable */}
                        {iAmHigherAuthority(0, dData?.role_weight) ? (
                            <>
                                <button
                                    className={`nav2-expand-btn${isActivePrefix('/dash') || isActive('/insights') ? ' is-active' : ''}${openSections.dash ? ' is-open' : ''}`}
                                    onClick={() => toggle('dash')}
                                    title="Dashboards"
                                >
                                    <i className="fe fe-bar-chart-2 nav2-expand-icon" />
                                    <span className="nav2-item__text">Dashboards</span>
                                    <i className="fe fe-chevron-right nav2-expand-chevron" />
                                </button>
                                <div className={`nav2-subnav${openSections.dash ? ' is-open' : ''}`}>
                                    {/* Single grid item: see .nav2-subnav in navigation-v2.css. */}
                                    <div className="nav2-subnav__inner">
                                        {iAmHigherAuthority(0, dData?.role_weight) ? (
                                            <Link to="/dash/main" className={`nav2-subitem${isActive('/dash/main') ? ' is-active' : ''}`} onClick={closeMobileNav}>Main</Link>
                                        ) : <></>}
                                        {iAmHigherAuthority(30, dData?.role_weight) ? (
                                            <Link to="/dash/corporate" className={`nav2-subitem${isActive('/dash/corporate') ? ' is-active' : ''}`} onClick={closeMobileNav}>Corporate</Link>
                                        ) : <></>}
                                        {dData?.acnt === 2 && iAmHigherAuthority(99, dData?.role_weight) ? (
                                            <Link to="/insights" className={`nav2-subitem${isActive('/insights') ? ' is-active' : ''}`} onClick={closeMobileNav}>Insights</Link>
                                        ) : <></>}
                                        <Link to="/dash/palpendants" className={`nav2-subitem${isActive('/dash/palpendants') ? ' is-active' : ''}`} onClick={closeMobileNav}>PalPendant Locator</Link>
                                    </div>
                                </div>
                            </>
                        ) : <></>}

                        {/* Active Alerts */}
                        {isCloud ? (
                            <Link to="/emergency" className={`nav2-item${isActive('/emergency') ? ' is-active' : ''}`} onClick={closeMobileNav} title="Active Alerts">
                                <i className="fe fe-bell nav2-item__icon" />
                                <span className="nav2-item__text">Active Alerts</span>
                                {alertCount > 0
                                    ? <span className="nav2-badge">{alertCount}</span>
                                    : <span className="nav2-badge nav2-badge--empty">0</span>}
                            </Link>
                        ) : (
                            <Link to="/alerts" className={`nav2-item${isActive('/alerts') ? ' is-active' : ''}`} onClick={closeMobileNav} title="Active Alerts">
                                <i className="fe fe-bell nav2-item__icon" />
                                <span className="nav2-item__text">Active Alerts</span>
                                {alertCount > 0
                                    ? <span className="nav2-badge">{alertCount}</span>
                                    : <span className="nav2-badge nav2-badge--empty">0</span>}
                            </Link>
                        )}

                        {/* EHR Tasks */}
                        {ehr.active && iAmHigherAuthority(10, dData?.role_weight) ? (
                            <Link to="/tasks/ehr" className={`nav2-item${isActive('/tasks/ehr') ? ' is-active' : ''}`} onClick={closeMobileNav} title="EHR Tasks">
                                <i className="fe fe-clipboard nav2-item__icon" />
                                <span className="nav2-item__text">EHR Tasks</span>
                                {ehr.stg_count !== 0 && <span className="nav2-badge">{ehr.stg_count}</span>}
                            </Link>
                        ) : <></>}
                    </div>

                    {/* ── COMMUNITY ── */}
                    {iAmHigherAuthority(10, dData?.role_weight) ? (
                        <div className="nav2-section">
                            <span className="nav2-section-label">Community</span>
                            <Link to="/community/residents" className={`nav2-item${isActivePrefix('/community/residents') ? ' is-active' : ''}`} onClick={closeMobileNav} title="Residents">
                                <i className="fe fe-home nav2-item__icon" />
                                <span className="nav2-item__text">Residents</span>
                            </Link>
                            <Link to="/community/areas" className={`nav2-item${isActivePrefix('/community/areas') ? ' is-active' : ''}`} onClick={closeMobileNav} title="Areas">
                                <i className="fe fe-map-pin nav2-item__icon" />
                                <span className="nav2-item__text">Areas</span>
                            </Link>
                            <Link to="/community/devices" className={`nav2-item${['/community/devices', '/community/server_devices', '/community/wander_devices', '/community/speaker_devices', '/community/system_devices'].some(p => location.pathname.startsWith(p)) ? ' is-active' : ''}`} onClick={closeMobileNav} title="Devices">
                                <i className="fe fe-tablet nav2-item__icon" />
                                <span className="nav2-item__text">Devices</span>
                            </Link>
                            {/* RTLS — only on campuses with an active vendor 14 (the
                                Inovonics RTLS provider). Pulled from the full vendor list
                                rather than a type filter since its type may change. */}
                            {getAllVendors().some((v) => v.vendor_active && v.vendor_id === 14) ? (
                                <Link to="/community/residents/locating" className={`nav2-item${isActive('/community/residents/locating') ? ' is-active' : ''}`} onClick={closeMobileNav} title="RTLS">
                                    <i className="fe fe-map nav2-item__icon" />
                                    <span className="nav2-item__text">RTLS</span>
                                </Link>
                            ) : null}
                        </div>
                    ) : <>
                            {getAllVendors().some((v) => v.vendor_active && v.vendor_id === 14) ? (
                                <div className="nav2-section">
                                    <span className="nav2-section-label">Community</span>
                                    <Link to="/community/residents/locating" className={`nav2-item${isActive('/community/residents/locating') ? ' is-active' : ''}`} onClick={closeMobileNav} title="RTLS">
                                        <i className="fe fe-map nav2-item__icon" />
                                        <span className="nav2-item__text">RTLS</span>
                                    </Link>
                                </div>
                            ) : null}
                    </>}

                    {/* ── REPORTS ──
                        Hide the whole section when no child links would render.
                        The outer condition must mirror the branch it lands in so
                        an impersonated caregiver doesn't see an empty expandable. */}
                    {iAmHigherAuthority(10, dData?.role_weight) ? (
                        <div className="nav2-section">
                            <span className="nav2-section-label">Reports</span>
                            <button
                                className={`nav2-expand-btn${isActivePrefix('/reports') ? ' is-active' : ''}${openSections.reports ? ' is-open' : ''}`}
                                onClick={() => toggle('reports')}
                                title="Reports"
                            >
                                <i className="fe fe-file-text nav2-expand-icon" />
                                <span className="nav2-item__text">Reports</span>
                                <i className="fe fe-chevron-right nav2-expand-chevron" />
                            </button>
                            <div className={`nav2-subnav${openSections.reports ? ' is-open' : ''}`}>
                                {/* Single grid item: see .nav2-subnav in navigation-v2.css. */}
                                <div className="nav2-subnav__inner">
                                    <Link to="/reports/alerts" className={`nav2-subitem${isActive('/reports/alerts') ? ' is-active' : ''}`} onClick={closeMobileNav}>Alert Details</Link>
                                    <Link to="/reports/hourly" className={`nav2-subitem${isActive('/reports/hourly') ? ' is-active' : ''}`} onClick={closeMobileNav}>Hourly Summary</Link>
                                    {/*<Link to="/reports/shifts" className={`nav2-subitem${isActive('/reports/shifts') ? ' is-active' : ''}`} onClick={closeMobileNav}>Shift Breakdown</Link>*/}
                                    <Link to="/reports/executive" className={`nav2-subitem${isActive('/reports/executive') ? ' is-active' : ''}`} onClick={closeMobileNav}>Executive</Link>
                                    <Link to="/reports/caregiver" className={`nav2-subitem${isActive('/reports/caregiver') ? ' is-active' : ''}`} onClick={closeMobileNav}>Caregiver Analysis</Link>
                                    <Link to="/reports/maintenance" className={`nav2-subitem${isActive('/reports/maintenance') ? ' is-active' : ''}`} onClick={closeMobileNav}>Maintenance</Link>
                                    {campus?.is_resident_checkin
                                        ? <Link to="/reports/checkin" className={`nav2-subitem${isActive('/reports/checkin') ? ' is-active' : ''}`} onClick={closeMobileNav}>Resident Check-in</Link>
                                        : <></>}
                                    {campus?.is_rounding
                                        ? <Link to="/reports/rounds" className={`nav2-subitem${isActive('/reports/rounds') ? ' is-active' : ''}`} onClick={closeMobileNav}>Staff Rounds</Link>
                                        : <></>}
                                    <Link to="/reports/environmental" className={`nav2-subitem${isActive('/reports/environmental') ? ' is-active' : ''}`} onClick={closeMobileNav}>Environment</Link>
                                    {campus?.is_grandstream ? (
                                        <Link to="/reports/call-logs" className={`nav2-subitem${isActive('/reports/call-logs') ? ' is-active' : ''}`} onClick={closeMobileNav}>Call Logs</Link>
                                    ) : null}
                                    <Link to="/reports/scheduled" className={`nav2-subitem${isActive('/reports/scheduled') ? ' is-active' : ''}`} onClick={closeMobileNav}>Scheduled</Link>
                                </div>
                            </div>
                        </div>
                    ) : <></>}

                    {/* ── ADMINISTRATION ── */}
                    {iAmHigherAuthority(10, dData?.role_weight) ? (
                    <div className="nav2-section">
                        <span className="nav2-section-label">Administration</span>

                        {iAmHigherAuthority(10, dData?.role_weight) ? (
                            <Link to="/users/usermanagement" className={`nav2-item${isActivePrefix('/users') ? ' is-active' : ''}`} onClick={closeMobileNav} title="Users">
                                <i className="fe fe-users nav2-item__icon" />
                                <span className="nav2-item__text">Users</span>
                            </Link>
                        ) : <></>}

                        {iAmHigherAuthority(30, dData?.role_weight) ? (
                            <Link to="/billing" className={`nav2-item${isActive('/billing') ? ' is-active' : ''}`} onClick={closeMobileNav} title="Billing">
                                <i className="fe fe-credit-card nav2-item__icon" />
                                <span className="nav2-item__text">Billing</span>
                            </Link>
                        ) : <></>}

                        {iAmHigherAuthority(20, dData?.role_weight) && (
                            <>
                                <button
                                    className={`nav2-expand-btn${isActivePrefix('/settings') ? ' is-active' : ''}${openSections.settings ? ' is-open' : ''}`}
                                    onClick={() => toggle('settings')}
                                    title="Settings"
                                >
                                    <i className="fe fe-settings nav2-expand-icon" />
                                    <span className="nav2-item__text">Settings</span>
                                    <i className="fe fe-chevron-right nav2-expand-chevron" />
                                </button>
                                <div className={`nav2-subnav${openSections.settings ? ' is-open' : ''}`}>
                                    {/* Single grid item: see .nav2-subnav in navigation-v2.css. */}
                                    <div className="nav2-subnav__inner">
                                        {(iAmHigherAuthority(20, dData?.role_weight)) ? (
                                            <Link to="/settings/system" className={`nav2-subitem${isActive('/settings/system') ? ' is-active' : ''}`} onClick={closeMobileNav}>System Settings</Link>
                                        ) : <></>}
                                        {campus?.careflow_v2 ? (
                                            <Link to="/settings/careflow" className={`nav2-subitem${isActive('/settings/careflow') ? ' is-active' : ''}`} onClick={closeMobileNav}>Careflow Settings</Link>
                                        ) : <></>}
                                        {[campus?.is_resident_checkin, campus?.is_resident_checkin, !campus?.careflow_v2].some((v) => v === 1 || v === true) ? (
                                            <Link to="/settings/wellness" className={`nav2-subitem${isActive('/settings/wellness') ? ' is-active' : ''}`} onClick={closeMobileNav}>Wellness Settings</Link>
                                        ) : <></>}
                                        <Link to="/settings/environmental" className={`nav2-subitem${isActive('/settings/environmental') ? ' is-active' : ''}`} onClick={closeMobileNav}>Environmental</Link>
                                        {iAmHigherAuthority(80, dData?.role_weight) ? (
                                            <Link to="/settings/shifts" className={`nav2-subitem${isActive('/settings/shifts') ? ' is-active' : ''}`} onClick={closeMobileNav}>Shift Hours</Link>
                                        ) : <></>}
                                        {campus?.is_cloud ? (
                                            <Link to="/settings/text_builder" className={`nav2-subitem${isActive('/settings/text_builder') ? ' is-active' : ''}`} onClick={closeMobileNav}>Custom Event Text</Link>
                                        ) : <></>}
                                        {iAmHigherAuthority(99, dData?.role_weight) && campus?.udp_broadcast ? (
                                            <Link to="/settings/lightctrl" className={`nav2-subitem${isActive('/settings/lightctrl') ? ' is-active' : ''}`} onClick={closeMobileNav}>Light Controller</Link>
                                        ) : <></>}
                                        {campus?.is_cloud ? (
                                            <Link to="/settings/palpendant" className={`nav2-subitem${isActive('/settings/palpendant') ? ' is-active' : ''}`} onClick={closeMobileNav}>PalPendant Profiles</Link>
                                        ) : <></>}
                                    </div>
                                </div>
                            </>
                        )}

                    </div>) : <></>}

                    {/* ── LINKS ── */}
                    <div className="nav2-section">
                        <Link to={`/profile/${dData?.username}`} className={`nav2-item${isActive(`/profile/${dData?.username}`) ? ' is-active' : ''}`} onClick={closeMobileNav} title="My Profile">
                            <i className="fe fe-user nav2-item__icon" />
                            <span className="nav2-item__text">My Profile</span>
                        </Link>
                        <a href="https://shop.palcare.com/" target="_blank" rel="noreferrer" className="nav2-item" onClick={closeMobileNav} title="PalCare Store">
                            <i className="fe fe-shopping-cart nav2-item__icon" />
                            <span className="nav2-item__text">PalCare Store</span>
                            <i className="fe fe-external-link nav2-item__external" />
                        </a>
                    </div>
                </div>

                {/* ── Footer ── */}
                <div className="nav2-footer">
                    {/* Interactive help — the single "I need help" entry point. The
                        old FAQ item pointed at the academy; that link moved into the
                        picker's footer so there is one place to look instead of two.

                        Sits in the footer rather than the LINKS list because it is an
                        action, not a destination, which is what the footer already
                        holds. Shaped like the Logout button beneath it so the two
                        read as one group, but in the guide's violet — the same hue as
                        the spotlight ring and the active step marker.

                        Wrapped in its own nav2-footer__actions row: the footer is a
                        column, and the button's `flex: 1` needs a row context to fill
                        the width rather than stretch vertically.

                        Gated by the same rules as the guide itself
                        (help/helpAvailability.js) so it can never open an empty
                        picker. */}
                    {helpAvailable ? (
                        <div className="nav2-footer__actions">
                            <button
                                type="button"
                                className={`nav2-help-btn${helpActive ? ' is-guide-active' : ''}`}
                                onClick={() => { closeMobileNav(); openHelpPicker(); }}
                                title="Step-by-step help with a task"
                                aria-pressed={helpActive}
                            >
                                <i className="fe fe-help-circle nav2-help-btn__icon" />
                                <span className="nav2-help-btn__text">Help</span>
                            </button>
                        </div>
                    ) : null}
                    {/* Theming is V2-only, and this nav is the V2-only surface,
                        so the toggle needs no `migrated` guard of its own — a
                        non-migrated user never renders NavigationV2. */}
                    <div className="nav2-theme-row">
                        <ThemeToggle />
                    </div>
                    <div className="nav2-footer__actions">
                        <button className="nav2-logout-btn" onClick={() => Logout(dData)} title="Logout">
                            <i className="fe fe-log-out nav2-logout-btn__icon" />
                            <span className="nav2-logout-btn__text">Logout</span>
                        </button>
                    </div>
                    <div className="nav2-version-info">
                        v. {dData.migrated ? import.meta.env.VITE_VERSION : '1.0.8'}
                        {iAmHigherAuthority(1000, dData?.role_weight) && ` · Build: ${import.meta.env.VITE_BUILD}`}
                    </div>
                </div>

                {import.meta.env.VITE_DEVMODE === 'true' && (
                    <div className="nav2-footer__dev-mode" style={{ textAlign: 'center', padding: '4px', color: 'var(--color-danger)', fontSize: '11px', fontWeight: 700 }}>
                        DEV MODE
                    </div>
                )}
            </aside>

            {/* ── Impersonate Role-Switch Dialog ── */}
            {impersonateOpen ? (
                <div
                    className="nav2-impersonate-overlay"
                    onClick={() => setImpersonateOpen(false)}
                >
                    <div className="nav2-impersonate-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Change Impersonation Role">
                        <h3 className="nav2-impersonate-dialog__title">Impersonate</h3>
                        <p className="nav2-impersonate-dialog__campus">{dData.corporateName || dData.campus}</p>
                        <div className="dp-drawer__field">
                            <label htmlFor="nav2-impersonate-role" className="dp-drawer__label">
                                Impersonating User Role
                            </label>
                            <select
                                id="nav2-impersonate-role"
                                value={impersonateRoleWeight}
                                onChange={(e) => setImpersonateRoleWeight(e.target.value)}
                                className="dp-drawer__select"
                            >
                                <option value={dData?.my_role_weight}>My Role</option>
                                {filteredRoles.map((r) => (
                                    <option key={r.user_role_weight} value={r.user_role_weight}>
                                        {r.user_role_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="nav2-impersonate-dialog__actions">
                            <button
                                type="button"
                                onClick={() => setImpersonateOpen(false)}
                                className="dp-btn dp-btn--secondary"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmImpersonate}
                                disabled={impersonating}
                                className="dp-btn dp-btn--primary"
                            >
                                {impersonating ? 'Switching\u2026' : 'Impersonate'}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default NavigationV2;
